import { Trash2 } from 'lucide-react';
import { t } from '../../../lib/i18n';
import { Button, TextField } from '../../../components/ui';
import { isValidEmail, isValidPhone } from '../../../components/phase2';

export type ContactDraft = { name: string; phone: string; email: string };
export const blankContact = (): ContactDraft => ({ name: '', phone: '', email: '' });

/** Why a row cannot be saved yet (an i18n key), or null. A contact needs a name and a phone number or an email. */
export function contactProblem(c: ContactDraft): string | null {
  if (c.name.trim() === '') return 'p3lw.contacts.nameNeeded';
  if (c.phone.trim() === '' && c.email.trim() === '') return 'p3lw.contacts.reachNeeded';
  if (c.phone.trim() !== '' && !isValidPhone(c.phone.trim())) return 'p3lw.contacts.phoneBad';
  if (c.email.trim() !== '' && !isValidEmail(c.email.trim())) return 'p3lw.contacts.emailBad';
  return null;
}

/** The request body rows for LoneWorkContactsPut. */
export const contactsBody = (rows: ContactDraft[]) =>
  rows.map((c) => ({ name: c.name.trim(), phone: c.phone.trim() === '' ? null : c.phone.trim(), email: c.email.trim() === '' ? null : c.email.trim() }));

/** Up to five contacts: name, phone, email. */
export function ContactRows({ rows, onChange, disabled, max = 5, label }: { rows: ContactDraft[]; onChange: (rows: ContactDraft[]) => void; disabled?: boolean; max?: number; label: string }) {
  const set = (n: number, patch: Partial<ContactDraft>) => onChange(rows.map((r, i) => (i === n ? { ...r, ...patch } : r)));
  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-medium text-slate-800">{label}</legend>
      {rows.length === 0 ? <p className="text-sm text-slate-600">{t('p3lw.contacts.none')}</p> : null}
      <ul className="space-y-3">
        {rows.map((r, n) => {
          const problem = contactProblem(r);
          return (
            <li key={n} className="rounded-lg border border-slate-200 p-3">
              <div className="grid gap-3 sm:grid-cols-3">
                <TextField label={t('p3lw.contacts.name', { n: n + 1 })} value={r.name} maxLength={120} disabled={disabled ?? false} onChange={(e) => set(n, { name: e.target.value })} />
                <TextField label={t('p3lw.contacts.phone', { n: n + 1 })} type="tel" value={r.phone} disabled={disabled ?? false} onChange={(e) => set(n, { phone: e.target.value })} />
                <TextField label={t('p3lw.contacts.email', { n: n + 1 })} type="email" value={r.email} disabled={disabled ?? false} onChange={(e) => set(n, { email: e.target.value })} />
              </div>
              {problem ? <p className="mt-2 text-sm text-red-700">{t(problem)}</p> : null}
              {disabled ? null : (
                <div className="mt-2">
                  <Button onClick={() => onChange(rows.filter((_, i) => i !== n))}>
                    <Trash2 aria-hidden="true" className="h-4 w-4" />
                    {t('p3lw.contacts.remove', { n: n + 1 })}
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {disabled || rows.length >= max ? null : <Button onClick={() => onChange([...rows, blankContact()])}>{t('p3lw.contacts.add')}</Button>}
    </fieldset>
  );
}
